/* eslint-disable react-refresh/only-export-components -- context + helpers only, no components to fast-refresh */
/**
 * Index view ("Both" | "NIFTY" | "BANKNIFTY") for the Options Analyst tabs
 * that render NIFTY and BANKNIFTY side by side (Option Indicator, OI Flow
 * Charts, Strike Charts, Dual View).
 *
 * `OptionsAnalystPage` owns the state and provides it; each of those four
 * pages only reads it to HIDE (CSS `display: none`) the excluded index's
 * section -- never to unmount it or change what it fetches/computes, so
 * switching views causes no refetch and loses no state. The default is
 * "both", so a page rendered outside `OptionsAnalystPage` (or by any test)
 * behaves exactly as before.
 */
import { createContext, useContext } from "react";

export type AnalystIndexView = "both" | "NIFTY" | "BANKNIFTY";

const AnalystIndexViewContext = createContext<AnalystIndexView>("both");

export const AnalystIndexViewProvider = AnalystIndexViewContext.Provider;

export function useAnalystIndexView(): AnalystIndexView {
  return useContext(AnalystIndexViewContext);
}

/**
 * Extra props for a `<Grid item>` / `<Box>` holding one index's section:
 * hidden when the other index is selected, and full-width (instead of half)
 * when it is the only one shown. `bp` is the breakpoint at which that
 * section normally goes half-width (`md` or `lg`).
 */
export function indexSectionProps(view: AnalystIndexView, symbol: "NIFTY" | "BANKNIFTY", bp: "md" | "lg") {
  const hidden = view !== "both" && view !== symbol;
  return {
    [bp]: view === "both" ? 6 : 12,
    sx: hidden ? { display: "none" } : undefined,
  } as { md?: number; lg?: number; sx?: { display: "none" } };
}
